'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isAdminPreview", "children", "location", "match", "onReady"];
import { useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import NavMarker from 'react-rhumb/NavMarker';
import { parse as parseParams } from 'hub-http/helpers/params';
import I18n from 'I18n';
import UIEmbeddedComponent from 'ui-addon-iframeable/embed/UIEmbeddedComponent';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { getIsCustomer, PublicOnly, getIsUngatedForUnifiedDetails } from '../../selectors/auth';
import { useScrollToTop } from 'marketplace-ui-common/hooks/useScrollToTop';
import { isSucceeded, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import NotFoundError from '../../components/error/NotFoundError';
import GenericError from '../../components/error/GenericError';
import NoAccessError from '../../components/error/NoAccessError';
import { AppDetailsPage } from '../../details/AppDetailsPage';
import { useListingView } from '../../utils/hooks/useListingView';
import HSPageViewTracker from '../../components/tracking/HSPageViewTracker';
import { LeadGenProvider } from '../../context/LeadGenContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DetailError = ({
  children,
  statusCode
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [children, /*#__PURE__*/_jsx(PublicOnly, {
    children: /*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx(PrerenderReady, {
        statusCode: statusCode
      })
    })
  })]
});
const DetailContainer = _ref => {
  let {
      isAdminPreview = false,
      children,
      location,
      match,
      onReady
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    pathname,
    search
  } = location;
  const {
    path
  } = match;
  const isUngatedForUnifiedDetails = useSelector(getIsUngatedForUnifiedDetails);
  const isCustomer = useSelector(getIsCustomer);
  useScrollToTop(pathname);
  const {
    listingView,
    loading,
    error: listingViewFetchError
  } = useListingView(isUngatedForUnifiedDetails);
  const listingViewFetchStatus = getDataFetchingClientRequestState(loading, listingViewFetchError);
  const {
    offering: {
      homemade
    },
    offeringId
  } = listingView;
  const {
    published
  } = parseParams(search.slice(1));
  const isPreview = published === 'false' || path === '/_preview/:offeringId' || isAdminPreview;
  useEffect(() => {
    if (isUninitialized(listingViewFetchStatus)) {
      document.title = I18n.text('appMarketplace.htmlTitle');
    }
  }, [listingViewFetchStatus]);
  useEffect(() => {
    if (isSucceeded(listingViewFetchStatus) && onReady) {
      onReady({
        receiveMessage: () => {}
      });
    }
  }, [listingViewFetchStatus, onReady]);
  if (listingViewFetchError) {
    if (listingViewFetchError !== null && listingViewFetchError !== void 0 && listingViewFetchError.networkError) {
      var _listingViewFetchErro;
      if (listingViewFetchError.networkError.status === 403 || ((_listingViewFetchErro = listingViewFetchError.cause) === null || _listingViewFetchErro === void 0 || (_listingViewFetchErro = _listingViewFetchErro.cause) === null || _listingViewFetchErro === void 0 || (_listingViewFetchErro = _listingViewFetchErro.cause) === null || _listingViewFetchErro === void 0 ? void 0 : _listingViewFetchErro.type) === 'UNAUTHORIZED_BETA_SLUG_ACCESS') {
        return /*#__PURE__*/_jsx(DetailError, {
          statusCode: 403,
          children: /*#__PURE__*/_jsx(NavMarker, {
            name: "DETAIL_NO_ACCESS_ERROR_LOAD",
            children: /*#__PURE__*/_jsx(NoAccessError, {
              use: isPreview ? 'preview' : 'default'
            })
          })
        });
      } else {
        return /*#__PURE__*/_jsx(DetailError, {
          statusCode: 404,
          children: /*#__PURE__*/_jsx(NavMarker, {
            name: "DETAIL_NOT_FOUND_ERROR_LOAD",
            children: /*#__PURE__*/_jsx(NotFoundError, {})
          })
        });
      }
    } else {
      return /*#__PURE__*/_jsx(DetailError, {
        statusCode: 400,
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "DETAIL_ERROR_LOAD",
          children: /*#__PURE__*/_jsx(GenericError, {})
        })
      });
    }
  }
  if (!isSucceeded(listingViewFetchStatus)) {
    return null;
  }
  const wrappedChildren = children || /*#__PURE__*/_jsx(AppDetailsPage, Object.assign({}, rest));
  if (isPreview) {
    return /*#__PURE__*/_jsx(LeadGenProvider, {
      offeringId: offeringId,
      homemade: homemade,
      isPreview: isPreview,
      children: wrappedChildren
    });
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(HSPageViewTracker, {
      addLang: isCustomer,
      overrideTrack: isCustomer,
      pathname: pathname
    }), /*#__PURE__*/_jsx(LeadGenProvider, {
      offeringId: offeringId,
      homemade: homemade,
      isPreview: isPreview,
      children: wrappedChildren
    })]
  });
};
DetailContainer.propTypes = {
  children: PropTypes.node,
  isAdminPreview: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired,
  onReady: PropTypes.func
};
export default UIEmbeddedComponent(DetailContainer, {
  enableTopLevelEmbed: true
});